import type { Transport } from '../types';
import { calculatedDurationMinutes } from './tripUtils';
import { cityLabel, matchesCity } from './placeNames';

/**
 * A multi-leg journey (MEL → SIN → CDG on one ticket) is stored as separate
 * transport legs. Only an explicit shared booking reference makes them one
 * ticket; proximity and `isConnection` alone cannot establish ownership.
 */

type Leg = Transport & { _dayDate?: string };
export { rentalOverlapsVisit } from './destinationItems';
const same = (a: Leg, b: Leg) => (a.id && b.id ? a.id === b.id : a.flightNum === b.flightNum && a.dep === b.dep);
const sameTicket = (a: Leg, b: Leg) => !a.isRental && !b.isRental && !!a.ref?.trim() && a.ref.trim().toLowerCase() === b.ref?.trim().toLowerCase();

export { sameTransportLeg } from "../../functions/src/bookingConflicts.mjs";

/** The whole journey `target` belongs to, in order; just `[target]` for a standalone leg. `all` is sorted by departure. */
export function journeyFor<T extends Leg>(target: T, all: T[]): T[] {
  if (target.isRental) return [target];
  all = all.filter(leg => !leg.isRental);
  const i = all.findIndex(leg => same(leg, target));
  if (i < 0) return [target];
  // A save can resolve before the Firestore subscription delivers its new list.
  all = all.map((leg, index) => index === i ? target : leg);
  let start = i;
  while (start > 0 && all[start].isConnection && sameTicket(all[start - 1], all[start])) start--;
  let end = i;
  while (end < all.length - 1 && all[end + 1].isConnection && sameTicket(all[end], all[end + 1])) end++;
  return all.slice(start, end + 1);
}

/** A short change of transport in one city, regardless of ticket/airport. */
export function layoverCity(prev: Leg, next: Leg): string | null {
  if (prev.isRental || next.isRental) return null;
  const arrival = prev.toCity || cityLabel(prev.to);
  const departure = next.fromCity || cityLabel(next.from);
  if (!arrival || !departure || /^[A-Z]{3}$/.test(arrival) || /^[A-Z]{3}$/.test(departure)) return null;
  const minutes = layoverMinutes(prev, next);
  if (minutes != null && (minutes < 0 || minutes > 24 * 60)) return null;
  return matchesCity(arrival, departure) || matchesCity(departure, arrival) ? arrival : null;
}

/** Minutes on the ground between two legs — at one airport, so one timezone; null when a time is missing. */
export function layoverMinutes(prev: Leg, next: Leg): number | null {
  return calculatedDurationMinutes(
    { fromCode: prev.toCode, from: prev.to, fromCity: prev.toCity, toCode: next.fromCode, to: next.from, toCity: next.fromCity },
    prev.arr, next.dep, prev.arrTz, next.depTz,
  );
}

export const formatMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60), m = minutes % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
};
